import React, { useEffect, useRef, useState } from "react";
import "./VideoRecommendationsPanel.css";

const DEFAULT_TABS = ["To Do", "New Releases"];

function VideoRecommendationsPanel({
  tabs = DEFAULT_TABS,
  itemsByTab = {},
  onItemClick,
  resources = [],
  onViewAllResources,
  onResourceClick,
  height,
}) {
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const activeItemRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!tabs.length) return;
    if (!tabs.includes(activeTab)) {
      setActiveTab(tabs[0]);
    }
  }, [tabs, activeTab]);

  const items = itemsByTab[activeTab] || [];

  // Derive the active item's id so we only scroll when the active item changes
  const activeItemId = items.find(i => i.active)?.id;

  useEffect(() => {
    if (listRef.current && activeItemRef.current) {
      // Scroll the list container so the active item appears at the top
      listRef.current.scrollTo({
        top: activeItemRef.current.offsetTop - listRef.current.offsetTop,
        behavior: "smooth",
      });
    }
  }, [activeItemId]);

  return (
    <div
      className={`vrp-container ${height ? "vrp-fixed-height" : ""}`.trim()}
      style={height ? { height: `${height}px` } : {}}
    >
      {/* Tab Bar */}
      <div className="vrp-tabs-wrapper">
        <div className="vrp-tabs">
          {tabs.map(tab => (
            <button
              key={tab}
              className={`vrp-tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Card Grid List — scrollable area */}
      <div className="vrp-list" ref={listRef}>
        {items.length === 0 ? (
          <div className="vrp-empty">No items available</div>
        ) : (
          items.map((item, index) => (
            <div
              key={item.id ?? index}
              ref={item.active ? activeItemRef : null}
              className={`vrp-item ${item.active ? "active" : ""} ${item.completed ? "watched" : ""}`}
              onClick={() => onItemClick && onItemClick(item)}
              role="button"
              tabIndex={0}
              aria-current={item.active ? "true" : undefined}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onItemClick && onItemClick(item);
                }
              }}
            >
              {/* Thumbnail */}
              <div className="vrp-thumbnail">
                {item.thumbnailUrl ? (
                  <img
                    src={item.thumbnailUrl}
                    alt={item.title || "Learning item"}
                  />
                ) : (
                  <div
                    className={`vrp-thumbnail-placeholder ${item.entityType === "program" ? "program" : "course"}`}
                  >
                    {item.entityType === "program" ? (
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <rect
                          x="4"
                          y="4"
                          width="16"
                          height="16"
                          rx="3"
                          stroke="rgba(255,255,255,0.7)"
                          strokeWidth="1.8"
                        />
                        <path
                          d="M8 12h8M12 8v8"
                          stroke="rgba(255,255,255,0.7)"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <polygon
                          points="5,3 19,12 5,21"
                          fill="rgba(255,255,255,0.6)"
                        />
                      </svg>
                    )}
                  </div>
                )}
                {item.duration && (
                  <span className="vrp-duration">{item.duration}</span>
                )}
                {item.completed && (
                  <span className="vrp-completed-badge">✓</span>
                )}
                {item.locked && <span className="vrp-locked-badge">🔒</span>}
              </div>

              {/* Info */}
              <div className="vrp-info">
                {item.badge && <p className="vrp-badge">{item.badge}</p>}
                {(item.views || item.date) && (
                  <p className="vrp-meta">
                    {[item.views, item.date].filter(Boolean).join(" | ")}
                  </p>
                )}
                <h3 className="vrp-title">{item.title || "Untitled"}</h3>
                {(item.progressLabel || item.duration) && (
                  <p className="vrp-detail">
                    {item.progressLabel || item.duration}
                  </p>
                )}
                {item.presenter && (
                  <p className="vrp-presenter">{item.presenter}</p>
                )}
                {item.completed && (
                  <p className="vrp-watched-indicator">✓ Watched</p>
                )}
                {item.progress != null && (
                  <div className="vrp-progress-bar">
                    <div
                      className="vrp-progress-fill"
                      style={{ width: `${item.progress}%` }}
                    />
                  </div>
                )}
                {item.pitchReviewRequired && (
                  <span className="vrp-pitch-review-chip">
                    Pitch review required
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Resources Section */}
      {resources.length > 0 && (
        <div className="vrp-resources">
          <div className="vrp-resources-header">
            <span className="vrp-resources-title">Resources</span>
            {onViewAllResources && (
              <button
                className="vrp-resources-view-all"
                onClick={onViewAllResources}
              >
                View all resources
              </button>
            )}
          </div>
          <div className="vrp-resources-list">
            {resources.map((resource, index) => (
              <div
                key={resource.id ?? index}
                className="vrp-resource-item"
                onClick={() => onResourceClick && onResourceClick(resource)}
                role={onResourceClick ? "button" : undefined}
                tabIndex={onResourceClick ? 0 : undefined}
                onKeyDown={e => {
                  if (onResourceClick && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    onResourceClick(resource);
                  }
                }}
              >
                {/* Checkbox icon */}
                <svg
                  className="vrp-resource-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <rect
                    x="1.5"
                    y="1.5"
                    width="13"
                    height="13"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                </svg>
                <span className="vrp-resource-label">{resource.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default VideoRecommendationsPanel;
